import { useEffect, useMemo, useState } from 'react';
import { fetchApi } from '../../api/fetch';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { Button } from '../../components/ui/button';

const PRESETS = [
  { label: '6:00 AM', value: '06:00', desc: 'Before the day starts' },
  { label: '7:30 AM', value: '07:30', desc: 'With morning coffee' },
  { label: '9:00 AM', value: '09:00', desc: 'At the start of the work day' },
];

export function WizardStep2Digest() {
  const { refetch } = useOnboarding();
  const [selected, setSelected] = useState('07:30');
  const [submitting, setSubmitting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [taskCount, setTaskCount] = useState<number | null>(null);

  const userTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );
  const isCustomSelected = !PRESETS.some((p) => p.value === selected);

  // Celebration banner: count the user's active tasks so we can acknowledge
  // what just landed during Phase 1 ("✓ 3 tasks added from your email").
  // Fire-and-forget — null while loading, banner hidden if count is 0.
  useEffect(() => {
    let alive = true;
    fetchApi<{ total?: number; data?: unknown[]; tasks?: unknown[] } | unknown[]>(
      '/tasks?limit=50',
    )
      .then((res) => {
        if (!alive) return;
        const count = Array.isArray(res)
          ? res.length
          : typeof res?.total === 'number'
            ? res.total
            : Array.isArray(res?.data)
              ? res.data.length
              : Array.isArray(res?.tasks)
                ? res.tasks.length
                : 0;
        setTaskCount(count);
      })
      .catch(() => {
        if (alive) setTaskCount(0);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await fetchApi('/onboarding/digest', {
        method: 'POST',
        body: JSON.stringify({ time: selected, timezone: userTimezone }),
      });
      refetch();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSkip() {
    setSkipping(true);
    try {
      await fetchApi('/onboarding/digest/skip', { method: 'POST', body: JSON.stringify({}) });
      refetch();
    } finally {
      setSkipping(false);
    }
  }

  return (
    <div className="flex h-screen w-full bg-white text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-zinc-200 dark:bg-zinc-800">
        <div className="h-full w-full bg-primary-500 transition-all" />
      </div>

      <div className="flex-1 flex items-start justify-center overflow-y-auto px-6 py-12 sm:py-20">
        <div className="w-full max-w-md">
          {taskCount !== null && taskCount > 0 && (
            <div className="mb-6 rounded-xl border border-primary-200 bg-primary-50 dark:border-primary-800/60 dark:bg-primary-950/40 px-4 py-3">
              <p className="text-sm text-primary-800 dark:text-primary-200">
                <span className="text-primary-700 dark:text-primary-400 font-semibold mr-2">✓</span>
                {taskCount === 1
                  ? '1 task added to your list'
                  : `${taskCount} tasks added to your list`}
                <span className="text-primary-700/70 dark:text-primary-300/70 ml-1">
                  — you'll see them after this last step.
                </span>
              </p>
            </div>
          )}

          <p className="text-xs font-semibold tracking-widest text-primary-600 dark:text-primary-400 uppercase mb-3">
            Step 2 of 2 — last one
          </p>
          <h1 className="text-3xl font-semibold mb-3">When should Danny brief you?</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-8 leading-relaxed">
            You'll get a short email every morning — your priorities, what's due,
            anything that needs a decision.
          </p>

          <div className="space-y-2.5 mb-6">
            {PRESETS.map((p) => {
              const isSelected = selected === p.value;
              return (
                <button
                  key={p.value}
                  onClick={() => setSelected(p.value)}
                  className={[
                    'w-full flex items-center gap-4 px-4 py-3.5 rounded-xl border text-left transition-all',
                    isSelected
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/40'
                      : 'border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-zinc-700',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                      isSelected
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-zinc-300 dark:border-zinc-600',
                    ].join(' ')}
                  >
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div>
                    <p
                      className={`text-sm font-semibold ${
                        isSelected
                          ? 'text-primary-700 dark:text-primary-300'
                          : 'text-zinc-900 dark:text-zinc-200'
                      }`}
                    >
                      {p.label}
                    </p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-500">{p.desc}</p>
                  </div>
                </button>
              );
            })}

            <div
              className={[
                'flex items-center gap-4 px-4 py-3.5 rounded-xl border transition-all',
                isCustomSelected
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/40'
                  : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900',
              ].join(' ')}
            >
              <div
                className={[
                  'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                  isCustomSelected
                    ? 'border-primary-500 bg-primary-500'
                    : 'border-zinc-300 dark:border-zinc-600',
                ].join(' ')}
              >
                {isCustomSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
              </div>
              <input
                type="time"
                value={isCustomSelected ? selected : ''}
                className="bg-zinc-100 border border-zinc-200 rounded-lg px-3 py-1.5 text-sm font-mono text-zinc-900 cursor-pointer dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-200 dark:[color-scheme:dark]"
                onChange={(e) => {
                  if (e.target.value) setSelected(e.target.value);
                }}
              />
              <span
                className={`text-sm ${
                  isCustomSelected
                    ? 'text-primary-700 dark:text-primary-300 font-semibold'
                    : 'text-zinc-500 dark:text-zinc-500'
                }`}
              >
                Custom time
              </span>
            </div>
          </div>

          <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-4 text-center">
            Times shown in your local time zone ({userTimezone})
          </p>

          <Button onClick={handleSubmit} disabled={submitting || skipping} size="lg" className="w-full">
            {submitting ? 'Saving…' : 'Looks good — take me to Danny →'}
          </Button>

          <div className="mt-4 text-center">
            <button
              onClick={handleSkip}
              disabled={submitting || skipping}
              className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-300 transition-colors disabled:opacity-50"
            >
              {skipping ? 'Skipping…' : 'Skip for now — I’ll set this up later →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
